import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Save, Plus, Trash2, ArrowUp, ArrowDown, RotateCcw, AlertTriangle,
  BarChart3, RefreshCw, TrendingUp, Copy, Check, ChevronDown, ChevronUp,
  ExternalLink,
  CreditCard, Send, Zap, Flame, Tv, Signal, HeartPulse, Banknote, Car,
  Droplet, Receipt, Wallet, Smartphone, Tag, Building2,
  Sparkles, Briefcase,
} from "lucide-react";

const ICON_MAP = {
  CreditCard, Send, Zap, Flame, Tv, Signal, HeartPulse, Banknote, Car,
  Droplet, Receipt, BarChart3, Wallet, Smartphone, Tag, Building2,
  Sparkles, Briefcase,
};

const KINDS = [
  { key: "bbps", label: "BBPS bill" },
  { key: "payout", label: "Payout" },
  { key: "custom", label: "Custom" },
];

const blankTile = () => ({
  slug: "",
  label: "",
  icon: "Zap",
  sub: "",
  highlight: false,
  kind: "bbps",
  target_path: "",
  login_next: "",
  utm_match: "",
});

export default function AdminLandingServices() {
  const [tiles, setTiles] = useState([]);
  const [allowedIcons, setAllowedIcons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [partnerOrigin, setPartnerOrigin] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/landing-services");
      setTiles(data.services || []);
      setAllowedIcons(data.allowed_icons || Object.keys(ICON_MAP));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  // Fetch the partner domain from domain config so ad URLs use the correct origin
  useEffect(() => {
    api.get("/admin/domain-config").then(({ data }) => {
      const pd = (data?.config?.partner_domain || "").trim();
      if (pd) {
        setPartnerOrigin(pd.startsWith("http") ? pd.replace(/\/$/, "") : `https://${pd}`);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, []);

  const update = (idx, key, value) => {
    setTiles((cur) => cur.map((t, i) => (i === idx ? { ...t, [key]: value } : t)));
  };
  const move = (idx, dir) => {
    setTiles((cur) => {
      const next = [...cur];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return cur;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };
  const remove = (idx) => setTiles((cur) => cur.filter((_, i) => i !== idx));
  const addTile = () => setTiles((cur) => [...cur, blankTile()]);

  const slugConflicts = useMemo(() => {
    const counts = {};
    for (const t of tiles) counts[t.slug] = (counts[t.slug] || 0) + 1;
    return Object.entries(counts).filter(([s, n]) => s && n > 1).map(([s]) => s);
  }, [tiles]);

  const invalidTiles = useMemo(() => tiles
    .map((t, i) => ({ i, t, bad: !t.slug || !t.label || !t.target_path || !t.target_path.startsWith("/") || t.target_path.startsWith("//") }))
    .filter((x) => x.bad), [tiles]);

  const save = async () => {
    if (slugConflicts.length > 0) {
      toast.error(`Duplicate slug(s): ${slugConflicts.join(", ")}`);
      return;
    }
    if (invalidTiles.length > 0) {
      toast.error("One or more tiles have missing slug/label/path or use a non-relative path.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/landing-services", { services: tiles });
      setTiles(data.services || []);
      toast.success("Landing services saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Reset to built-in defaults? This discards any custom services.")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/landing-services/reset");
      setTiles(data.services || []);
      toast.success("Reset to defaults");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  const tilesList = (
    <>
      {tiles.map((t, idx) => {
        const Icon = ICON_MAP[t.icon] || Zap;
        const isInvalid = !t.slug || !t.label || !t.target_path || !t.target_path.startsWith("/") || t.target_path.startsWith("//");
        return (
          <div
            key={idx}
            className={`surface-card p-4 ${isInvalid ? "border-destructive/40" : ""}`}
            data-testid={`tile-row-${idx}`}
          >
            <div className="flex items-start gap-4">
              <div className={`shrink-0 rounded-md grid place-items-center h-10 w-10 ${t.highlight ? "bg-secondary text-secondary-foreground" : "bg-accent text-primary"}`}>
                <Icon className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                <div className="md:col-span-2">
                  <Label className="text-[11px]">Slug</Label>
                  <Input data-testid={`tile-slug-${idx}`} value={t.slug} onChange={(e) => update(idx, "slug", e.target.value.toLowerCase().replace(/[^a-z0-9_]+/g, "_"))} placeholder="credit_card" className="mt-1 font-mono text-sm" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[11px]">Label</Label>
                  <Input data-testid={`tile-label-${idx}`} value={t.label} onChange={(e) => update(idx, "label", e.target.value)} placeholder="Credit Card" className="mt-1" />
                </div>
                <div className="md:col-span-1">
                  <Label className="text-[11px]">Icon</Label>
                  <select data-testid={`tile-icon-${idx}`} value={t.icon} onChange={(e) => update(idx, "icon", e.target.value)} className="mt-1 w-full text-sm h-9 rounded-md border border-input bg-background px-2">
                    {(allowedIcons || []).map((ic) => (<option key={ic} value={ic}>{ic}</option>))}
                  </select>
                </div>
                <div className="md:col-span-1">
                  <Label className="text-[11px]">Kind</Label>
                  <select data-testid={`tile-kind-${idx}`} value={t.kind} onChange={(e) => update(idx, "kind", e.target.value)} className="mt-1 w-full text-sm h-9 rounded-md border border-input bg-background px-2">
                    {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[11px]">Subtitle</Label>
                  <Input data-testid={`tile-sub-${idx}`} value={t.sub} onChange={(e) => update(idx, "sub", e.target.value)} placeholder="All banks · BBPS" className="mt-1 text-sm" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[11px]">Target path</Label>
                  <Input data-testid={`tile-target-${idx}`} value={t.target_path} onChange={(e) => update(idx, "target_path", e.target.value)} placeholder="/bbps/v2?category=credit_card" className="mt-1 font-mono text-xs" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[11px]">UTM match (comma-separated)</Label>
                  <Input data-testid={`tile-utm-${idx}`} value={t.utm_match} onChange={(e) => update(idx, "utm_match", e.target.value)} placeholder="credit_card,cc_bill" className="mt-1 font-mono text-xs" />
                </div>
              </div>
              <div className="shrink-0 flex flex-col gap-1.5 items-end pt-5">
                <label className="text-[11px] flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" data-testid={`tile-highlight-${idx}`} checked={!!t.highlight} onChange={(e) => update(idx, "highlight", e.target.checked)} />
                  Highlight
                </label>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-accent disabled:opacity-30" data-testid={`tile-up-${idx}`}>
                    <ArrowUp className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                  <button type="button" onClick={() => move(idx, 1)} disabled={idx === tiles.length - 1} className="p-1 rounded hover:bg-accent disabled:opacity-30" data-testid={`tile-down-${idx}`}>
                    <ArrowDown className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                  <button type="button" onClick={() => remove(idx)} className="p-1 rounded hover:bg-destructive/10 text-destructive" data-testid={`tile-remove-${idx}`}>
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            </div>
            <UrlBuilder tile={t} idx={idx} partnerOrigin={partnerOrigin} />
          </div>
        );
      })}
      {tiles.length === 0 && (
        <div className="surface-card p-6 text-center text-sm text-muted-foreground">
          No tiles. Click <b>Add tile</b> or <b>Reset defaults</b>.
        </div>
      )}
    </>
  );

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Admin · Landing page</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            Landing services & ad routing
          </h1>
          <p className="text-muted-foreground mt-2 max-w-3xl text-sm">
            Configure the tiles shown on <code className="font-mono">/</code> for ad-clickers from
            FB / Google. Each tile can route to any internal page; <b>UTM match</b> patterns
            highlight + auto-scroll the matching tile when a visitor lands with{" "}
            <code className="font-mono">?svc=…</code> or{" "}
            <code className="font-mono">?utm_campaign=…</code>. Highlighted tiles get a ring + pulse.
          </p>
        </div>
      </div>

      <Tabs defaultValue="config" className="mt-6">
        <TabsList data-testid="landing-tabs">
          <TabsTrigger value="config" data-testid="tab-config">Configuration</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">
            <BarChart3 className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
            Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="mt-4">
          <div className="flex items-center justify-end gap-2 flex-wrap mb-3">
            <Button variant="outline" onClick={addTile} data-testid="add-tile">
              <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add tile
            </Button>
            <Button variant="outline" onClick={resetDefaults} data-testid="reset-defaults">
              <RotateCcw className="h-4 w-4 mr-1" strokeWidth={1.5} /> Reset defaults
            </Button>
            <Button onClick={save} disabled={saving || loading || slugConflicts.length > 0 || invalidTiles.length > 0} data-testid="save-landing-services">
              <Save className="h-4 w-4 mr-1" strokeWidth={1.5} />
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>

          {slugConflicts.length > 0 && (
            <div className="mb-4 border border-destructive/40 bg-destructive/5 rounded-md p-3 text-sm flex items-start gap-2" data-testid="dup-warning">
              <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" strokeWidth={1.5} />
              Duplicate slug(s): <b>{slugConflicts.join(", ")}</b>. Each tile needs a unique slug.
            </div>
          )}

          <div className="grid gap-3" data-testid="landing-services-list">{tilesList}</div>
        </TabsContent>

        <TabsContent value="analytics" className="mt-4">
          <LandingAnalytics />
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function LandingAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [windowDays, setWindowDays] = useState(7);

  const load = async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get(`/admin/landing-services/analytics?window_days=${windowDays}`);
      setData(payload);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load analytics");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [windowDays]);

  const totals = data?.totals || { views: 0, clicks: 0, conversions: 0, conversion_value: 0, window_days: windowDays, events_total: 0 };
  const ctrAll = totals.views ? (totals.clicks * 100 / totals.views).toFixed(2) : "0.00";
  const cvrAll = totals.clicks ? (totals.conversions * 100 / totals.clicks).toFixed(2) : "0.00";

  return (
    <div className="space-y-4" data-testid="landing-analytics">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground">Window:</span>
        {[1, 7, 30, 90].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setWindowDays(d)}
            className={`text-xs px-2.5 py-1 rounded-full border ${windowDays === d ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}
            data-testid={`window-${d}`}
          >{d === 1 ? "Today" : `${d}d`}</button>
        ))}
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="reload-analytics" className="ml-auto">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} /> Reload
        </Button>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard label="Tile views" value={totals.views} testId="kpi-views" />
        <KpiCard label="Tile clicks" value={totals.clicks} sub={`CTR ${ctrAll}%`} testId="kpi-clicks" />
        <KpiCard label="Conversions" value={totals.conversions} sub={`CVR ${cvrAll}%`} testId="kpi-conversions" />
        <KpiCard label="Revenue attributed" value={`₹${Number(totals.conversion_value || 0).toLocaleString("en-IN")}`} testId="kpi-revenue" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <AnalyticsTable
          title="Per-service funnel"
          subtitle="Best-performing tiles by views → clicks → conversions"
          rows={data?.per_slug || []}
          headerKey="slug"
          headerLabel="Service"
          testId="table-per-slug"
          icon={TrendingUp}
        />
        <AnalyticsTable
          title="Per-campaign attribution"
          subtitle="Which UTM campaigns drove engagement"
          rows={data?.per_campaign || []}
          headerKey="utm_campaign"
          headerLabel="Campaign"
          testId="table-per-campaign"
          icon={Sparkles}
        />
      </div>
      <AnalyticsTable
        title="Per-source breakdown"
        subtitle="Traffic origin (facebook · google · direct)"
        rows={data?.per_source || []}
        headerKey="utm_source"
        headerLabel="Source"
        testId="table-per-source"
        icon={BarChart3}
      />
      {totals.events_total === 0 && !loading && (
        <div className="surface-card p-6 text-center text-sm text-muted-foreground" data-testid="analytics-empty">
          No events captured in this window yet. Send a test visit with{" "}
          <code className="font-mono">?utm_campaign=demo&svc=payout</code> to start populating the funnel.
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, sub, testId }) {
  return (
    <div className="surface-card p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-display text-2xl sm:text-3xl font-bold mt-1">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">{sub}</div>}
    </div>
  );
}

function AnalyticsTable({ title, subtitle, rows, headerKey, headerLabel, testId, icon: Icon }) {
  return (
    <div className="surface-card p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />}
        <h3 className="font-semibold text-sm">{title}</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-3">{subtitle}</p>
      {rows.length === 0 ? (
        <div className="text-xs text-muted-foreground py-4 text-center">No data in window</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-2 pr-3 font-medium">{headerLabel}</th>
                <th className="py-2 px-2 font-medium text-right">Views</th>
                <th className="py-2 px-2 font-medium text-right">Clicks</th>
                <th className="py-2 px-2 font-medium text-right">Conv</th>
                <th className="py-2 pl-2 font-medium text-right">₹ Value</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 12).map((r, i) => (
                <tr key={i} className="border-b border-border/40 hover:bg-accent/30">
                  <td className="py-1.5 pr-3 font-mono">{r[headerKey] || "(none)"}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{r.views || 0}</td>
                  <td className="py-1.5 px-2 text-right font-mono">
                    {r.clicks || 0}
                    {typeof r.ctr === "number" && <span className="ml-1 text-[10px] text-muted-foreground">({r.ctr}%)</span>}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono">
                    {r.conversions || 0}
                    {typeof r.cvr === "number" && <span className="ml-1 text-[10px] text-muted-foreground">({r.cvr}%)</span>}
                  </td>
                  <td className="py-1.5 pl-2 text-right font-mono">₹{Number(r.conversion_value || 0).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function UrlBuilder({ tile, idx, partnerOrigin }) {
  const [open, setOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState("");

  // Tokens from the tile's UTM-match field become campaign suggestions.
  const tokens = useMemo(() => {
    return (tile.utm_match || "")
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
  }, [tile.utm_match]);

  // Use the partner-facing domain so URLs are pasteable into ad campaigns as-is.
  // Falls back to REACT_APP_BACKEND_URL or current origin only when partner domain isn't configured.
  const origin = partnerOrigin
    || (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")
    || (typeof window !== "undefined" ? window.location.origin : "");

  // Build a URL that goes directly to the target_path with extra query params merged in.
  const buildDirectUrl = (extraParams = {}) => {
    const targetPath = tile.target_path || "/";
    const qIdx = targetPath.indexOf("?");
    const path = qIdx >= 0 ? targetPath.slice(0, qIdx) : targetPath;
    const existing = new URLSearchParams(qIdx >= 0 ? targetPath.slice(qIdx + 1) : "");
    for (const [k, v] of Object.entries(extraParams)) {
      if (v) existing.set(k, v);
    }
    const qs = existing.toString();
    return `${origin}${path}${qs ? "?" + qs : ""}`;
  };

  const urls = useMemo(() => {
    const out = [];
    if (tile.slug && tile.target_path) {
      out.push({
        key: "svc",
        label: "Direct service URL",
        helper: "Opens the service page directly. Redirects to login if not authenticated.",
        url: buildDirectUrl({ svc: tile.slug }),
      });
    }
    for (const tok of tokens) {
      out.push({
        key: `utm-${tok}`,
        label: `Campaign · ${tok}`,
        helper: "Paste into Facebook / Google ad manager. Opens the service page directly with campaign tracking.",
        url: buildDirectUrl({
          svc: tile.slug,
          utm_campaign: tok,
          utm_source: "facebook",
          utm_medium: "cpc",
        }),
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin, tile.slug, tile.target_path, tokens]);

  const copy = async (key, url) => {
    try {
      // navigator.clipboard works only over HTTPS / localhost. Fall back to a
      // textarea-select trick for other contexts.
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus(); ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopiedKey(key);
      toast.success("URL copied to clipboard");
      setTimeout(() => setCopiedKey((k) => (k === key ? "" : k)), 1800);
    } catch {
      toast.error("Couldn't copy — long-press to select manually");
    }
  };

  if (!tile.slug) return null;

  return (
    <div className="mt-3 pt-3 border-t border-dashed border-border" data-testid={`url-builder-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
        data-testid={`url-builder-toggle-${idx}`}
      >
        {open ? <ChevronUp className="h-3.5 w-3.5" strokeWidth={1.5} /> : <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.5} />}
        Ad URLs to copy ({urls.length})
      </button>

      {open && (
        <div className="mt-3 space-y-2">
          {!partnerOrigin && (
            <div className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid={`url-domain-hint-${idx}`}>
              Using preview URL. Set your <b>Partner Domain</b> in <a href="/admin/domains" className="underline">Domain settings</a> so ad URLs point to your live domain.
            </div>
          )}
          {urls.length === 0 && (
            <div className="text-xs text-muted-foreground">
              No URL suggestions yet — set a <code className="font-mono">slug</code>,{" "}
              <code className="font-mono">target path</code>, and{" "}
              <code className="font-mono">UTM match</code> patterns to generate ad-ready URLs.
            </div>
          )}
          {urls.map((u) => (
            <div
              key={u.key}
              className="rounded-md border border-border bg-muted/20 p-2.5 flex items-center gap-2"
              data-testid={`url-row-${idx}-${u.key}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-semibold text-foreground">{u.label}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{u.helper}</div>
                <input
                  type="text"
                  readOnly
                  value={u.url}
                  data-testid={`url-input-${idx}-${u.key}`}
                  onClick={(e) => e.currentTarget.select()}
                  className="mt-1.5 w-full font-mono text-[11px] rounded bg-background border border-input px-2 py-1 truncate"
                />
              </div>
              <div className="shrink-0 flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => copy(u.key, u.url)}
                  className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded border border-border hover:bg-accent font-medium"
                  data-testid={`url-copy-${idx}-${u.key}`}
                >
                  {copiedKey === u.key ? (
                    <><Check className="h-3.5 w-3.5 text-success" strokeWidth={1.5} /> Copied</>
                  ) : (
                    <><Copy className="h-3.5 w-3.5" strokeWidth={1.5} /> Copy</>
                  )}
                </button>
                <a
                  href={u.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1.5 rounded border border-border hover:bg-accent"
                  data-testid={`url-open-${idx}-${u.key}`}
                  title="Open in new tab"
                >
                  <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
